/**
 * Universal reliability/error model (frontend).
 *
 * Every teacher action — not just Google Classroom sync — can be described by
 * a single structured shape so the UI can decide consistently whether to show
 * a retry affordance, a reconnect prompt, or a "your work is saved" message.
 *
 * This builds on the Phase 2 Google codes (`lib/googleErrorMessages.ts`) and
 * generalizes them: network, validation, not-found/already-deleted, stale,
 * partial-success, grading/OCR, export and upload failures all map here.
 *
 * Keep the canonical codes/categories in sync with
 * `server/lib/reliability/errorModel.js`.
 */
import { GOOGLE_ERROR_MESSAGES, extractGoogleErrorCode } from '../googleErrorMessages';

export type ErrorCategory =
  | 'NETWORK'
  | 'AUTH'
  | 'PERMISSION'
  | 'NOT_FOUND'
  | 'STALE'
  | 'VALIDATION'
  | 'PARTIAL'
  | 'TRANSIENT'
  | 'GRADING'
  | 'EXPORT'
  | 'UPLOAD'
  | 'UNKNOWN';

/** Canonical, stable error codes shared with the server. */
export const ERROR_CODES = {
  NETWORK_TIMEOUT: 'NETWORK_TIMEOUT',
  NETWORK_UNAVAILABLE: 'NETWORK_UNAVAILABLE',
  GOOGLE_RECONNECT_REQUIRED: 'GOOGLE_RECONNECT_REQUIRED',
  GOOGLE_PERMISSION_DENIED: 'GOOGLE_PERMISSION_DENIED',
  GOOGLE_SCOPE_MISSING: 'GOOGLE_SCOPE_MISSING',
  GOOGLE_RATE_LIMITED: 'GOOGLE_RATE_LIMITED',
  GOOGLE_TEMPORARY_FAILURE: 'GOOGLE_TEMPORARY_FAILURE',
  GOOGLE_TIMEOUT: 'GOOGLE_TIMEOUT',
  AUTH_REQUIRED: 'AUTH_REQUIRED',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
  RESOURCE_ALREADY_DELETED: 'RESOURCE_ALREADY_DELETED',
  RESOURCE_STALE: 'RESOURCE_STALE',
  PARTIAL_SUCCESS: 'PARTIAL_SUCCESS',
  LOCAL_STATE_OUT_OF_SYNC: 'LOCAL_STATE_OUT_OF_SYNC',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  EXPORT_FAILED: 'EXPORT_FAILED',
  UPLOAD_FAILED: 'UPLOAD_FAILED',
  GRADING_FAILED: 'GRADING_FAILED',
  OCR_FAILED: 'OCR_FAILED',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

export interface ReliabilityError {
  code: string;
  category: ErrorCategory;
  retryable: boolean;
  recoverable: boolean;
  userMessage: string;
  status?: number;
}

/** Optional context that refines messaging (e.g. NOT_FOUND during a delete). */
export interface ErrorContext {
  action?: 'delete' | 'export' | 'upload' | 'grade' | 'ocr' | 'sync' | 'generic';
}

interface CodeDescriptor {
  code: string;
  category: ErrorCategory;
  retryable: boolean;
  recoverable: boolean;
}

/**
 * Map the many server-typed codes onto the canonical model. Anything not
 * listed falls through to status-based or message-based classification.
 */
const CODE_TABLE: Record<string, CodeDescriptor> = {
  // ----- Auth / reconnect (never auto-retry; recoverable by reconnecting) ---
  GOOGLE_RECONNECT_REQUIRED: r('GOOGLE_RECONNECT_REQUIRED', 'AUTH', false, true),
  AUTH_RECONNECT_REQUIRED: r('GOOGLE_RECONNECT_REQUIRED', 'AUTH', false, true),
  GOOGLE_REFRESH_FAILED: r('GOOGLE_RECONNECT_REQUIRED', 'AUTH', false, true),
  GOOGLE_TOKEN_INVALID: r('GOOGLE_RECONNECT_REQUIRED', 'AUTH', false, true),
  GOOGLE_NOT_LINKED: r('GOOGLE_RECONNECT_REQUIRED', 'AUTH', false, true),
  AUTH_REQUIRED: r('AUTH_REQUIRED', 'AUTH', false, true),
  // ----- Permission / scope (never auto-retry) ------------------------------
  GOOGLE_SCOPE_MISSING: r('GOOGLE_SCOPE_MISSING', 'PERMISSION', false, true),
  AUTH_PERMISSION_REQUIRED: r('GOOGLE_SCOPE_MISSING', 'PERMISSION', false, true),
  GOOGLE_PERMISSION_DENIED: r('GOOGLE_PERMISSION_DENIED', 'PERMISSION', false, true),
  // ----- Transient (safe to retry) ------------------------------------------
  GOOGLE_RATE_LIMITED: r('GOOGLE_RATE_LIMITED', 'TRANSIENT', true, true),
  GOOGLE_TEMPORARY_FAILURE: r('GOOGLE_TEMPORARY_FAILURE', 'TRANSIENT', true, true),
  GOOGLE_TIMEOUT: r('GOOGLE_TIMEOUT', 'NETWORK', true, true),
  GOOGLE_UNKNOWN_ERROR: r('GOOGLE_TEMPORARY_FAILURE', 'TRANSIENT', true, true),
  // ----- Not found (recoverable via sync; "already deleted" for deletes) ----
  CLASSROOM_COURSE_NOT_FOUND: r('RESOURCE_NOT_FOUND', 'NOT_FOUND', false, true),
  CLASSROOM_ASSIGNMENT_NOT_FOUND: r('RESOURCE_NOT_FOUND', 'NOT_FOUND', false, true),
  CLASSROOM_TOPIC_NOT_FOUND: r('RESOURCE_NOT_FOUND', 'NOT_FOUND', false, true),
  CLASSROOM_ROSTER_NOT_FOUND: r('RESOURCE_NOT_FOUND', 'NOT_FOUND', false, true),
  CLASSROOM_RUBRIC_NOT_FOUND: r('RESOURCE_NOT_FOUND', 'NOT_FOUND', false, true),
  TOPIC_NOT_FOUND: r('RESOURCE_NOT_FOUND', 'NOT_FOUND', false, true),
  RUBRIC_NOT_FOUND: r('RESOURCE_NOT_FOUND', 'NOT_FOUND', false, true),
  RESOURCE_NOT_FOUND: r('RESOURCE_NOT_FOUND', 'NOT_FOUND', false, true),
  // ----- Stale / mapping (recoverable via sync) -----------------------------
  COURSE_ID_MAPPING_ERROR: r('RESOURCE_STALE', 'STALE', false, true),
  CLASSROOM_COURSE_ID_STALE: r('RESOURCE_STALE', 'STALE', false, true),
  CLASSROOM_COURSE_RECONCILIATION_AMBIGUOUS: r('RESOURCE_STALE', 'STALE', false, true),
  // ----- Validation (user must fix input; not retryable, not recoverable) ---
  INVALID_STATE: r('VALIDATION_ERROR', 'VALIDATION', false, false),
  INVALID_TITLE: r('VALIDATION_ERROR', 'VALIDATION', false, false),
  INVALID_DUE_DATE: r('VALIDATION_ERROR', 'VALIDATION', false, false),
  INVALID_MAX_POINTS: r('VALIDATION_ERROR', 'VALIDATION', false, false),
  TOPIC_NAME_REQUIRED: r('VALIDATION_ERROR', 'VALIDATION', false, false),
  ASSIGNMENT_PATCH_NO_FIELDS: r('VALIDATION_ERROR', 'VALIDATION', false, false),
  COURSEWORK_PATCH_INVALID_FIELD: r('VALIDATION_ERROR', 'VALIDATION', false, false),
  RUBRIC_NO_CRITERIA: r('VALIDATION_ERROR', 'VALIDATION', false, false),
  RUBRIC_CRITERION_TITLE_REQUIRED: r('VALIDATION_ERROR', 'VALIDATION', false, false),
  RUBRIC_LEVELS_REQUIRED: r('VALIDATION_ERROR', 'VALIDATION', false, false),
  RUBRIC_LEVEL_TITLE_REQUIRED: r('VALIDATION_ERROR', 'VALIDATION', false, false),
  RUBRIC_LEVEL_INVALID_POINTS: r('VALIDATION_ERROR', 'VALIDATION', false, false),
  // ----- Domain failures (retryable server-side hiccups) --------------------
  COURSE_SYNC_FAILED: r('UNKNOWN_ERROR', 'UNKNOWN', true, true),
  COURSE_CREATE_FAILED: r('UNKNOWN_ERROR', 'UNKNOWN', true, true),
  COURSE_PATCH_FAILED: r('UNKNOWN_ERROR', 'UNKNOWN', true, true),
  COURSE_DELETE_FAILED: r('UNKNOWN_ERROR', 'UNKNOWN', true, true),
  ASSIGNMENTS_LOAD_FAILED: r('UNKNOWN_ERROR', 'UNKNOWN', true, true),
  ASSIGNMENT_PATCH_FAILED: r('UNKNOWN_ERROR', 'UNKNOWN', true, true),
  ASSIGNMENT_DELETE_FAILED: r('UNKNOWN_ERROR', 'UNKNOWN', true, true),
  ROSTER_LOAD_FAILED: r('UNKNOWN_ERROR', 'UNKNOWN', true, true),
  TOPICS_LIST_FAILED: r('UNKNOWN_ERROR', 'UNKNOWN', true, true),
  TOPIC_CREATE_FAILED: r('UNKNOWN_ERROR', 'UNKNOWN', true, true),
  RUBRIC_LIST_FAILED: r('UNKNOWN_ERROR', 'UNKNOWN', true, true),
  RUBRIC_CREATE_FAILED: r('UNKNOWN_ERROR', 'UNKNOWN', true, true),
  RUBRIC_UPDATE_FAILED: r('UNKNOWN_ERROR', 'UNKNOWN', true, true),
  RUBRIC_DELETE_FAILED: r('UNKNOWN_ERROR', 'UNKNOWN', true, true),
  EXPORT_FAILED: r('EXPORT_FAILED', 'EXPORT', true, true),
  UPLOAD_FAILED: r('UPLOAD_FAILED', 'UPLOAD', true, true),
  GRADING_FAILED: r('GRADING_FAILED', 'GRADING', true, true),
  OCR_FAILED: r('OCR_FAILED', 'GRADING', true, true),
};

function r(
  code: string,
  category: ErrorCategory,
  retryable: boolean,
  recoverable: boolean
): CodeDescriptor {
  return { code, category, retryable, recoverable };
}

/** Friendly text for the canonical codes. Returns null for codes where the
 *  error's own message is preferred (generic server failures). */
function canonicalMessage(code: string, context?: ErrorContext): string | null {
  switch (code) {
    case ERROR_CODES.NETWORK_TIMEOUT:
      return 'The request took too long. Check your connection and try again — your work is saved.';
    case ERROR_CODES.NETWORK_UNAVAILABLE:
      return 'Cannot reach DoneGrading. Check your internet connection and try again — your work is saved.';
    case ERROR_CODES.GOOGLE_RECONNECT_REQUIRED:
      return GOOGLE_ERROR_MESSAGES.GOOGLE_RECONNECT_REQUIRED;
    case ERROR_CODES.GOOGLE_SCOPE_MISSING:
      return GOOGLE_ERROR_MESSAGES.GOOGLE_SCOPE_MISSING;
    case ERROR_CODES.GOOGLE_RATE_LIMITED:
      return GOOGLE_ERROR_MESSAGES.GOOGLE_RATE_LIMITED;
    case ERROR_CODES.GOOGLE_TEMPORARY_FAILURE:
      return GOOGLE_ERROR_MESSAGES.GOOGLE_TEMPORARY_FAILURE;
    case ERROR_CODES.GOOGLE_PERMISSION_DENIED:
      return GOOGLE_ERROR_MESSAGES.GOOGLE_PERMISSION_DENIED;
    case ERROR_CODES.GOOGLE_TIMEOUT:
      return GOOGLE_ERROR_MESSAGES.GOOGLE_TIMEOUT;
    case ERROR_CODES.RESOURCE_ALREADY_DELETED:
      return 'This item no longer exists — it looks like it was already deleted. Your view will refresh on the next sync.';
    case ERROR_CODES.RESOURCE_NOT_FOUND:
      return context?.action === 'delete'
        ? 'This item no longer exists — it may already have been deleted. Sync to refresh.'
        : 'This item could not be found. Sync from Classroom and try again.';
    case ERROR_CODES.RESOURCE_STALE:
      return 'This item is out of date. Sync from Classroom to refresh, then try again.';
    case ERROR_CODES.PARTIAL_SUCCESS:
      return 'Some items finished and some did not. Already-completed work was kept — retry only what failed.';
    default:
      return null;
  }
}

function messageString(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === 'string') return err;
  return '';
}

function looksLikeTimeout(msg: string, name?: string): boolean {
  return name === 'AbortError' || /timeout|timed out|ETIMEDOUT|deadline/i.test(msg);
}

function looksLikeNetworkDown(msg: string, name?: string): boolean {
  return (
    name === 'TypeError' ||
    /failed to fetch|networkerror|cannot reach|network request failed|connection|offline|ECONNRESET|ENOTFOUND/i.test(
      msg
    )
  );
}

/**
 * Classify ANY thrown error into the universal reliability model. Reads
 * ScanApiError-shaped objects ({ status, body: { code, googleErrorCode } }),
 * plain network errors, and bare strings.
 */
export function classifyError(err: unknown, context?: ErrorContext): ReliabilityError {
  const anyErr = (err ?? {}) as {
    status?: number;
    body?: { code?: string; googleErrorCode?: string; error?: string; message?: string };
    code?: string;
    name?: string;
  };
  const msg = messageString(err);
  const status = typeof anyErr.status === 'number' ? anyErr.status : undefined;

  // 1) Structured Google code wins (Phase 2 server contract).
  const googleCode = extractGoogleErrorCode(err);

  // 2) Server-typed code (body.code) or top-level code.
  const rawCode = googleCode || anyErr.body?.code || anyErr.code;
  let descriptor = rawCode ? CODE_TABLE[rawCode] : undefined;

  // 3) Status-based fallback when no recognized code.
  if (!descriptor && typeof status === 'number') {
    descriptor = descriptorForStatus(status);
  }

  // 4) Bare network/timeout errors (no status, no code).
  if (!descriptor && status === undefined && !rawCode) {
    if (looksLikeTimeout(msg, anyErr.name)) {
      descriptor = r(ERROR_CODES.NETWORK_TIMEOUT, 'NETWORK', true, true);
    } else if (looksLikeNetworkDown(msg, anyErr.name)) {
      descriptor = r(ERROR_CODES.NETWORK_UNAVAILABLE, 'NETWORK', true, true);
    }
  }

  if (!descriptor) {
    descriptor = r(ERROR_CODES.UNKNOWN_ERROR, 'UNKNOWN', false, false);
  }

  // Refine NOT_FOUND → ALREADY_DELETED when the action is a deletion.
  let code = descriptor.code;
  if (code === ERROR_CODES.RESOURCE_NOT_FOUND && context?.action === 'delete') {
    code = ERROR_CODES.RESOURCE_ALREADY_DELETED;
  }

  const userMessage =
    canonicalMessage(code, context) ||
    (msg && !isGenericMessage(msg) ? msg : null) ||
    defaultMessageForCategory(descriptor.category);

  return {
    code,
    category: descriptor.category,
    retryable: descriptor.retryable,
    recoverable: descriptor.recoverable,
    userMessage,
    status,
  };
}

function descriptorForStatus(status: number): CodeDescriptor | undefined {
  if (status === 408 || status === 504)
    return r(ERROR_CODES.NETWORK_TIMEOUT, 'NETWORK', true, true);
  if (status === 429 || status === 500 || status === 502 || status === 503) {
    return r(ERROR_CODES.GOOGLE_TEMPORARY_FAILURE, 'TRANSIENT', true, true);
  }
  if (status === 401) return r(ERROR_CODES.AUTH_REQUIRED, 'AUTH', false, true);
  if (status === 403) return r(ERROR_CODES.PERMISSION_DENIED, 'PERMISSION', false, true);
  if (status === 404) return r(ERROR_CODES.RESOURCE_NOT_FOUND, 'NOT_FOUND', false, true);
  if (status === 409) return r(ERROR_CODES.RESOURCE_STALE, 'STALE', false, true);
  if (status === 400 || status === 422) {
    return r(ERROR_CODES.VALIDATION_ERROR, 'VALIDATION', false, false);
  }
  return undefined;
}

function isGenericMessage(msg: string): boolean {
  return /^(could not delete|sync failed|something went wrong|request failed|error)\.?$/i.test(
    msg.trim()
  );
}

function defaultMessageForCategory(category: ErrorCategory): string {
  switch (category) {
    case 'NETWORK':
      return 'Network problem. Try again — your work is saved.';
    case 'AUTH':
      return 'Your session expired. Sign in or reconnect to continue.';
    case 'PERMISSION':
      return 'You do not have permission for this action.';
    case 'NOT_FOUND':
      return 'That item could not be found.';
    case 'STALE':
      return 'This item is out of date. Sync to refresh and try again.';
    case 'VALIDATION':
      return 'Please review the highlighted fields and try again.';
    case 'TRANSIENT':
      return 'A temporary problem occurred. Try again in a moment.';
    case 'GRADING':
      return 'Grading could not be completed for this item. You can retry it.';
    case 'EXPORT':
      return 'The export could not be completed. Already-finished work was kept — retry the rest.';
    case 'UPLOAD':
      return 'The upload could not be completed. Try again.';
    case 'PARTIAL':
      return 'Some work finished and some did not. Retry only what failed.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

/** Convenience: should the caller offer a retry button for this error? */
export function isRetryable(err: unknown, context?: ErrorContext): boolean {
  return classifyError(err, context).retryable;
}
